import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/**
 * Hook global pour gérer le bouton / geste retour natif Android (@capacitor/app).
 * - Ferme d'abord les modales, tiroirs ou overlays ouverts (Radix Dialog / AlertDialog / Sidebar mobile).
 * - Sinon remonte dans l'historique React Router (navigate(-1)).
 * - Si l'utilisateur est sur la page racine (/home ou /login), minimise l'application proprement.
 */
export const useAndroidBackButton = (): void => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const listenerPromise = App.addListener('backButton', ({ canGoBack }) => {
      // 1. Check if a Radix modal, AlertDialog, or mobile sheet/sidebar is currently open
      const openDialogs = document.querySelectorAll<HTMLElement>(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]'
      );

      if (openDialogs.length > 0) {
        const topDialog = openDialogs[openDialogs.length - 1];
        // Dispatch Escape key event which Radix UI listens to for closing dialogs/sheets
        const escEvent = new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          keyCode: 27,
          which: 27,
          bubbles: true,
          cancelable: true,
        });
        topDialog.dispatchEvent(escEvent);
        document.dispatchEvent(escEvent);
        return;
      }

      // 2. Navigate back in React Router if not on root pages
      const currentPath = pathnameRef.current;
      const isRootPage = currentPath === '/home' || currentPath === '/' || currentPath === '/login';

      if (!isRootPage) {
        if (canGoBack || window.history.length > 1) {
          navigate(-1);
        } else {
          navigate('/home', { replace: true });
        }
        return;
      }

      // 3. On root page, minimize the app gracefully instead of killing the activity
      App.minimizeApp().catch(() => {
        App.exitApp();
      });
    });

    return () => {
      listenerPromise.then((listener) => listener.remove()).catch(() => {});
    };
  }, [navigate]);
};

export default useAndroidBackButton;
